import { Fragment, useState } from 'react';
import { Badge } from '../../components/Badge';
import { holdEnding, lessonsNeedingAction, needsAttention, type PlanAttention } from './attention';
import { planLabel, weekdayNames } from './labels';
import type { LearningPlan, PlanLesson } from '../../types/learning-plans';

const time = (s: string) => new Date(s).toLocaleString('ar-SA-u-ca-gregory-nu-latn', { timeZone: 'Asia/Riyadh', dateStyle: 'medium', timeStyle: 'short' });
const money = (n: number, currency: string) =>
  /^[A-Z]{3}$/.test(currency)
    ? new Intl.NumberFormat('ar-SA-u-nu-latn', { style: 'currency', currency }).format(n / 100)
    : `${(n / 100).toFixed(2)} ${currency}`;

type View = '' | 'needs_setup' | 'held' | 'paid' | 'closed' | Exclude<PlanAttention, 'needs_setup'>;

// Lifecycle views first; the two attention views appear only while they hold
// something (or a worklist link opened them), so the bar stays short.
const views: { value: View; label: string; attention?: boolean }[] = [
  { value: '', label: 'الكل' },
  { value: 'needs_setup', label: 'يحتاج معلّمًا وجدولًا' },
  { value: 'held', label: 'بانتظار الدفع' },
  { value: 'paid', label: 'شغّالة' },
  { value: 'closed', label: 'منتهية' },
  { value: 'hold_ending', label: 'مهلة تنتهي خلال يوم', attention: true },
  { value: 'needs_lessons', label: 'حصص تحتاج إجراء', attention: true },
];

function inView(r: LearningPlan, view: string): boolean {
  if (!view) return true;
  if (view === 'closed') return r.status === 'completed' || r.status === 'cancelled';
  if (view === 'needs_setup' || view === 'hold_ending' || view === 'needs_lessons') return needsAttention(r, view);
  return r.status === view;
}

const statusTone = (status: string) =>
  status === 'paid' ? 'good' : status === 'expired' ? 'danger' : status === 'requesting' ? 'accent' : 'neutral';

const teachers = (r: LearningPlan): string => {
  const names = r.schedule?.length
    ? r.schedule.map((s) => s.tutorName)
    : r.lessons.filter((l) => l.status === 'held' || l.status === 'confirmed').map((l) => l.tutorName ?? '');
  return [...new Set(names.filter(Boolean))].join('، ') || '—';
};

function nextStep(r: LearningPlan): string {
  if (r.status === 'requesting' || r.status === 'expired') return 'تعيين المعلّم والجدول';
  if (r.status === 'held') return r.holdUntil ? `بانتظار دفع الأسرة حتى ${time(r.holdUntil)}` : 'بانتظار دفع الأسرة';
  const pending = lessonsNeedingAction(r);
  if (pending) return `${pending} حصص تحتاج حسم حضور أو بديلة`;
  if (r.status === 'paid') {
    const next = r.lessons.filter((l) => l.status === 'confirmed' && Date.parse(l.startTime) > Date.now())
      .sort((a, b) => a.startTime.localeCompare(b.startTime))[0];
    return next ? `الحصة القادمة ${time(next.startTime)}` : 'لا حصص قادمة';
  }
  return '—';
}

/**
 * The family requests as one row each: who, which plan, where it stands and
 * the one thing to do next. Everything else opens under the row.
 */
export function RequestsTable({ requests, view, onView, onSetup, onManage, onLesson, onCancel, onPayment, onRenew }: {
  requests: LearningPlan[];
  view: string;
  onView: (view: string) => void;
  onSetup: (r: LearningPlan) => void;
  onManage: (r: LearningPlan) => void;
  onLesson: (r: LearningPlan, lesson: PlanLesson) => void;
  onCancel: (r: LearningPlan) => void;
  onPayment: (r: LearningPlan) => void;
  onRenew: (r: LearningPlan) => void;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const term = search.trim().toLocaleLowerCase('ar');
  const matches = (r: LearningPlan) => !term || [r.studentName, r.offer.nameAr, r.offer.nameEn]
    .some((value) => value.toLocaleLowerCase('ar').includes(term));
  const count = (v: View) => requests.filter((r) => inView(r, v)).length;
  const shown = requests.filter((r) => inView(r, view) && matches(r));

  return (
    <div className="space-y-3">
      <section className="panel space-y-3 p-3" aria-label="تصفية طلبات الأسر">
        <div role="tablist" aria-label="حالة الطلب" className="flex flex-wrap gap-1.5">
          {views.filter((v) => !v.attention || count(v.value) > 0 || view === v.value).map((v) => (
            <button key={v.value || 'all'} type="button" role="tab" aria-selected={view === v.value}
              className={`rounded-full border px-3 py-1 text-sm ${view === v.value ? 'border-accent bg-accent-wash font-semibold text-accent' : 'border-rule text-ink-2 hover:bg-surface-2'}`}
              onClick={() => onView(v.value)}>
              {v.label} <span className="tabular text-xs">{count(v.value)}</span>
            </button>
          ))}
        </div>
        <label className="block text-sm">
          <span className="sr-only">البحث باسم الطالب أو الخطة</span>
          <input className="input" type="search" value={search} placeholder="ابحث باسم الطالب أو الخطة"
            aria-label="البحث باسم الطالب أو الخطة" onChange={(e) => setSearch(e.target.value)} />
        </label>
        <p className="text-xs text-ink-3" role="status">عرض {shown.length} من {requests.length} طلبات</p>
      </section>

      {requests.length === 0 && <p className="panel p-6 text-center text-ink-2">لا توجد طلبات من الأسر حتى الآن.</p>}
      {requests.length > 0 && shown.length === 0 && (
        <p className="panel p-6 text-center text-ink-2">لا توجد طلبات مطابقة. اختر حالة أخرى أو غيّر البحث.</p>
      )}

      {shown.length > 0 && (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-xs text-ink-3">
              <tr>
                <th className="px-3 py-2 text-start font-medium">الطالب</th>
                <th className="hidden px-3 py-2 text-start font-medium md:table-cell">الخطة</th>
                <th className="px-3 py-2 text-start font-medium">الحالة</th>
                <th className="hidden px-3 py-2 text-start font-medium lg:table-cell">المعلّم</th>
                <th className="hidden px-3 py-2 text-start font-medium sm:table-cell">الخطوة التالية</th>
                <th className="px-3 py-2"><span className="sr-only">الإجراء</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {shown.map((r) => {
                const expanded = open === r.id;
                const upcoming = r.lessons.some((l) => l.status === 'confirmed' && Date.parse(l.startTime) > Date.now());
                return (
                  <Fragment key={r.id}>
                    <tr data-request-id={r.id} className={expanded ? 'bg-surface-2' : undefined}>
                      <td className="px-3 py-2.5">
                        <button type="button" className="text-start" aria-expanded={expanded} aria-controls={`request-${r.id}`}
                          onClick={() => setOpen(expanded ? null : r.id)}>
                          <span className="block font-medium text-ink">{r.studentName}</span>
                          <span className="block text-xs text-ink-3">
                            {planLabel(r.kind)}{r.renewsRequestId ? ' · تجديد' : ''}
                            <span className="md:hidden"> · {r.offer.nameAr || r.offer.nameEn}</span>
                          </span>
                        </button>
                      </td>
                      <td className="hidden px-3 py-2.5 md:table-cell">{r.offer.nameAr || r.offer.nameEn}</td>
                      <td className="px-3 py-2.5">
                        <span className="flex flex-wrap gap-1">
                          <Badge tone={statusTone(r.status)}>{planLabel(r.status)}</Badge>
                          {holdEnding(r) && <Badge tone="danger">المهلة تنتهي قريبًا</Badge>}
                        </span>
                      </td>
                      <td className="hidden px-3 py-2.5 text-ink-2 lg:table-cell">{teachers(r)}</td>
                      <td className="hidden px-3 py-2.5 text-ink-2 sm:table-cell">{nextStep(r)}</td>
                      <td className="px-3 py-2.5 text-end">
                        {r.status === 'requesting' || r.status === 'expired' ? (
                          <button type="button" className="btn btn-primary whitespace-nowrap" aria-label="تعيين المعلّم والجدول" onClick={() => onSetup(r)}>
                            <span aria-hidden="true" className="sm:hidden">تعيين</span>
                            <span aria-hidden="true" className="hidden sm:inline">تعيين المعلّم والجدول</span>
                          </button>
                        ) : r.status === 'held' ? (
                          <button type="button" className="btn btn-primary" onClick={() => onPayment(r)}>تسجيل الدفع</button>
                        ) : r.status === 'paid' && lessonsNeedingAction(r) > 0 ? (
                          <button type="button" className="btn btn-primary whitespace-nowrap" onClick={() => setOpen(r.id)}>حسم الحصص</button>
                        ) : r.status === 'paid' && upcoming ? (
                          <button type="button" className="btn btn-secondary whitespace-nowrap" onClick={() => onManage(r)}>إدارة الخطة</button>
                        ) : (
                          <button type="button" className="btn btn-secondary whitespace-nowrap" aria-expanded={expanded}
                            onClick={() => setOpen(expanded ? null : r.id)}>{expanded ? 'إخفاء' : 'التفاصيل'}</button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr id={`request-${r.id}`} className="bg-surface-2">
                        <td colSpan={6} className="px-3 pb-4 pt-1">
                          <RequestDetails request={r} upcoming={upcoming} onManage={onManage} onLesson={onLesson} onCancel={onCancel} onRenew={onRenew} onSetup={onSetup} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RequestDetails({ request: r, upcoming, onManage, onLesson, onCancel, onRenew, onSetup }: {
  request: LearningPlan;
  upcoming: boolean;
  onManage: (r: LearningPlan) => void;
  onLesson: (r: LearningPlan, lesson: PlanLesson) => void;
  onCancel: (r: LearningPlan) => void;
  onRenew: (r: LearningPlan) => void;
  onSetup: (r: LearningPlan) => void;
}) {
  const trackName = (key: string) => r.offer.tracks.find((t) => t.key === key)?.name ?? key;
  const lessons = r.lessons.filter((l) => l.status !== 'released');
  return (
    <div className="space-y-3 rounded-lg border border-rule bg-surface p-3">
      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
        <div><dt className="text-xs text-ink-3">الخطوة التالية</dt><dd>{nextStep(r)}</dd></div>
        <div>
          <dt className="text-xs text-ink-3">المبلغ والدورة</dt>
          <dd>{money(r.amountCents, r.currency)} · {r.startDate ?? 'لم يُتفق على البداية'} → {r.endDate ?? 'دورة من 28 يومًا'}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-xs text-ink-3">الأوقات المناسبة للأسرة</dt>
          <dd className="mt-1 flex flex-wrap gap-1.5">
            {r.windows.map((w) => (
              <span key={`${w.weekday}-${w.startLocal}`} className="rounded-md bg-surface-2 px-2 py-0.5 text-xs tabular-nums">
                {weekdayNames[w.weekday]} {w.startLocal}–{w.endLocal}
              </span>
            ))}
          </dd>
        </div>
        {r.schedule?.map((s) => (
          <div key={s.trackKey} className="sm:col-span-2">
            <dt className="text-xs text-ink-3">{trackName(s.trackKey)} · المعلّم الأساسي والمواعيد</dt>
            <dd>{s.tutorName} · {s.times.map((t) => `${weekdayNames[t.weekday]} ${t.startLocal}`).join('، ')}</dd>
          </div>
        ))}
        {r.balances.map((b) => (
          <div key={b.trackKey}>
            <dt className="text-xs text-ink-3">{trackName(b.trackKey)} · الرصيد</dt>
            <dd>
              {b.completed}/{b.purchased} مكتملة
              {b.owed > 0 && ` · ${b.owed} بديلة مستحقة`}
              {b.pendingResolution > 0 && ` · ${b.pendingResolution} بانتظار حسم الحضور`}
              {b.forfeited > 0 && ` · ${b.forfeited} مستهلكة بالغياب`}
              {` · تعديلات الطالب ${b.changesUsed}/${b.changesAllowed}`}
            </dd>
          </div>
        ))}
      </dl>
      {(r.isDemoPayment || (!r.providerRef && r.paymentMode === 'demo')) && (
        <p className="text-xs text-ink-3">دفع تجريبي — لم يُحصّل أي مبلغ{r.isDemoPayment && r.providerRef ? ` · ${r.providerRef}` : ''}</p>
      )}
      {r.providerRef?.startsWith('offline-') && <p className="text-xs text-ink-3">دفعة مسجّلة بواسطة الإدارة · التفاصيل في سجل العمليات</p>}

      {lessons.length > 0 && (
        <div>
          <h3 className="mb-1 text-xs font-medium text-ink-3">الحصص ({lessons.length})</h3>
          <ul className="max-h-72 divide-y divide-rule overflow-y-auto rounded-lg border border-rule">
            {lessons.map((l) => (
              <li key={l.id} data-lesson-id={l.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <span>
                  <span className="tabular-nums">{time(l.startTime)}</span> · {l.tutorName} · {trackName(l.trackKey)}
                  {' · '}<span className={l.status === 'attendance_pending' || l.status === 'owed' ? 'font-medium text-danger' : 'text-ink-2'}>{planLabel(l.status)}</span>
                  {l.reason && <span className="text-ink-3"> · {l.reason}</span>}
                </span>
                {['confirmed', 'owed', 'attendance_pending'].includes(l.status) && (
                  <button type="button" className={`btn ${l.status === 'confirmed' ? 'btn-secondary' : 'btn-primary'}`} onClick={() => onLesson(r, l)}>
                    {l.status === 'attendance_pending' ? 'حسم الحضور' : l.status === 'owed' ? 'ترتيب حصة بديلة' : 'تسجيل غياب / إيقاف إداري'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {r.status === 'held' && <button type="button" className="btn btn-secondary" onClick={() => onSetup(r)}>تعديل الجدول قبل الدفع</button>}
        {r.kind === 'plan' && ['paid', 'completed'].includes(r.status) && <button type="button" className="btn btn-secondary" onClick={() => onRenew(r)}>تجديد الخطة</button>}
        {r.status === 'paid' && upcoming && (
          <button type="button" className="btn btn-secondary" onClick={() => onManage(r)}>إدارة الخطة</button>
        )}
        {['requesting', 'held', 'expired'].includes(r.status) && (
          <button type="button" className="btn btn-secondary text-danger" onClick={() => onCancel(r)}>إغلاق الطلب غير المدفوع</button>
        )}
      </div>
    </div>
  );
}
